import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on component mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Simple auth status check
  const checkAuthStatus = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/auth/status', {
        method: 'GET',
        credentials: 'include', 
        cache: 'no-cache' // Prevent caching
      });

      if (response.ok) {
        const data = await response.json();
      
        if (data.success && data.isAuthenticated) {
          setCurrentUser(data.user);
        } else {
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
    } catch (err) {
      console.error('Error checking authentication status:', err);
      setError('Failed to verify authentication status');
      setCurrentUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Login function
  const login = async (loginData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/v1/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
        credentials: 'include',
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Ошибка при входе');
      }
      
      // Set the user data from the response
      setCurrentUser(data.user);
      return { success: true, data };
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Произошла ошибка при входе');
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/logout', {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        setCurrentUser(null);
        return { success: true };
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Ошибка при выходе');
      }
    } catch (err) {
      console.error('Logout error:', err);
      setError(err.message || 'Произошла ошибка при выходе');
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Provider value
  const value = {
    currentUser,
    isAuthenticated: !!currentUser,
    isLoading,
    error,
    login,
    logout,
    refreshUser: checkAuthStatus // Simple alias for consistency
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 